import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { AlbumEntity, LikeEntity, PhotoEntity } from '@interpals/api-web';

import { createLike, deleteLike, getPhotoLikes, LIKES_FETCH_LIMIT } from '../likes/actions';

import {
    ALBUMS_FETCH_LIMIT,
    createAlbum,
    deleteAlbum,
    deleteAlbumPhoto,
    fetchAlbum,
    fetchAlbums,
    fetchAllAlbums,
    fetchPhoto,
    fetchPhotoCount,
    fetchRecentPhotos,
    moderationDeletePhoto,
    movePhotoToAlbum,
    RECENT_PHOTOS_FETCH_LIMIT,
    updateAlbumDetails,
    updateAlbumPhotosOrder,
    updatePhotoDescription,
    uploadPhotoToAlbumNew,
} from './actions';

// Nested photo objects are contained in the following slices:
// - albums
// - posts
// - recent photos list
// - messages

// There will be duplication, e.g. photos in albums and posts may belong to same album,
// photos in recent photos and albums will overlap too, but this is probably less work
// than trying to keep everything in sync by normalizing photos across slices into their own
// array and then having to update them in multiple places (esp. coordinating removal)

export interface PhotoAlbumsState {
    // List of sorted albums per user
    //
    // Each album contains these relevant fields
    // - photos (nested entities)
    // - photo_ids (ids of photos in album, for ordering)
    // - cover (album cover photo)
    // - album_cover_id (id of album cover photo)
    // - photo_total (total number of photos in album)
    // Adding/removing photos and albums should take these into account.
    albumsByUid: { [key: string]: AlbumEntity[] };
    hasMoreAlbumsByUid: { [key: string]: boolean };

    // List of photos by user, sorted by date
    //
    // We are keeping this separate and storing the full photo entities here
    // even though they may duplicate the entities in the albums list above.
    // This is because the logic to manage an entirely normalized store of
    // photos turned out to be much more complex.
    recentPhotosByUserId: {
        [key: string]: {
            photos: PhotoEntity[];
            total?: number;
            hasMore: boolean;
        };
    };

    likesByPhotoId: {
        [key: string]: {
            likes: LikeEntity[];
            hasMore: boolean;
        };
    };

    postsById: { [key: string]: PhotoEntity[] };
}

const initialState = {
    albumsByUid: {},
    hasMoreAlbumsByUid: {},
    photosByAlbumId: {},
    recentPhotosByUserId: {},
    likesByPhotoId: {},
    postsById: {},
} as PhotoAlbumsState;

const removePhotoFromAlbum = (userAlbums: AlbumEntity[], photoId: string, albumId: string) =>
    userAlbums.map((album) => {
        if (album.id === albumId) {
            // Remove from nested photos
            album.photos = album.photos?.filter((p) => p.id !== photoId);

            // Remove from photo list, decrement total
            if (album.photo_ids) {
                album.photo_ids = album.photo_ids.filter((id) => id !== photoId);
                album.photo_total = album.photo_ids.length;
            }

            // Replace album cover if it was the deleted photo
            if (album.album_cover_id === photoId) {
                const newCoverId = album.photo_ids?.[0] ?? undefined;
                album.album_cover_id = newCoverId;
                if (album.cover) {
                    album.cover = album.photos?.find((p) => p.id === newCoverId);
                }
            }
        }

        return album;
    });

const addPhotoToAlbum = (userAlbums: AlbumEntity[], photo: PhotoEntity, albumId: string) =>
    userAlbums.map((album) => {
        if (album.id === albumId) {
            // Add to nested photos
            album.photos = [...(album?.photos ?? []), photo];

            // Add to photo list, increment total
            if (album.photo_ids) {
                if (!album.photo_ids.includes(photo.id)) {
                    album.photo_ids = [...album.photo_ids, photo.id];
                    album.photo_total = album.photo_ids.length;
                }
            }

            // Set as album cover if it's the first photo
            if (album.photo_ids?.length === 1) {
                album.album_cover_id = photo.id;
                album.cover = photo;
            }
        }

        return album;
    });

const replaceAlbumPreservePhotos = (userAlbums: AlbumEntity[], album: AlbumEntity) =>
    userAlbums.map((a) => {
        if (a.id === album.id) {
            // If we already loaded photos, let's preserve them
            album.photos = a.photos?.length ? a.photos : album.photos;

            return album;
        }

        return a;
    });

const replaceAlbumPhotosOrder = (userAlbums: AlbumEntity[], album: AlbumEntity) =>
    userAlbums.map((a) => {
        if (a.id === album.id) {
            if (album.photo_ids) {
                album.photos = album.photo_ids
                    .map((id) => a.photos?.find((photo) => photo.id === id))
                    .filter((photo) => photo !== undefined) as PhotoEntity[];
            }

            return album;
        }

        return a;
    });

const replacePhotoInAlbum = (userAlbums: AlbumEntity[], photo: PhotoEntity) =>
    userAlbums.map((album) => {
        if (album.id === photo.aid) {
            // Replace in nested photos
            album.photos = album.photos?.map((p) => (p.id === photo.id ? photo : p));

            // Replace album cover if it was the replaced photo
            if (album.album_cover_id === photo.id) {
                album.album_cover_id = photo.id;
                if (album.cover) {
                    album.cover = photo;
                }
            }
        }

        return album;
    });

const replacePhoto = (state: PhotoAlbumsState, photo: PhotoEntity) => {
    const albumsByUid = state.albumsByUid[photo.uid];
    if (albumsByUid) {
        state.albumsByUid[photo.uid] = replacePhotoInAlbum(albumsByUid, photo);
    }

    const recentPhotosByUserId = state.recentPhotosByUserId[photo.uid];

    if (recentPhotosByUserId) {
        state.recentPhotosByUserId[photo.uid] = {
            ...recentPhotosByUserId,
            photos: recentPhotosByUserId.photos.map((p) => (p.id === photo.id ? photo : p)),
        };
    }
};

const updatePhotoLikeStatus = (state: PhotoAlbumsState, photo: PhotoEntity, liked: boolean) => {
    replacePhoto(state, {
        ...photo,
        likes_comments: {
            ...photo.likes_comments,
            user_liked: liked,
            likes: liked ? photo.likes_comments.likes + 1 : Math.max(0, photo.likes_comments.likes - 1),
        },
    });
};

const photoAlbumsSlice = createSlice({
    name: 'photoAlbums',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        //
        // Album cases
        //
        builder.addCase(fetchAlbums.fulfilled, (state, { payload: albums, meta }) => {
            if (meta.arg.offset === 0) {
                state.albumsByUid[meta.arg.userId] = albums;
            } else {
                state.albumsByUid[meta.arg.userId] = [...state.albumsByUid[meta.arg.userId], ...albums];
            }

            state.hasMoreAlbumsByUid[meta.arg.userId] = albums.length === ALBUMS_FETCH_LIMIT;
        });

        builder.addCase(fetchAllAlbums.fulfilled, (state, { payload: albums, meta }) => {
            // Save previously loaded albums to extract nested photos from them
            const loadedAlbums = state.albumsByUid[meta.arg.userId] ?? [];

            // Make lookup table of loaded albums by id to avoid find()
            const albumsById = loadedAlbums.reduce<{ [key: string]: AlbumEntity }>((acc, album) => {
                acc[album.id] = album;

                return acc;
            }, {});

            // Preserve photos loaded in previous requests
            // take photos from each of loaded albums and replace them in new albums
            state.albumsByUid[meta.arg.userId] = albums.map((album) => {
                const loadedAlbum = albumsById[album.id];
                if (loadedAlbum) {
                    album.photos = loadedAlbum.photos;
                }

                return album;
            });

            state.hasMoreAlbumsByUid[meta.arg.userId] = false;
        });

        builder.addCase(fetchAlbum.fulfilled, (state, { payload: album }) => {
            // Replace album in user albums list (if exists), otherwise add it
            const userAlbums = state.albumsByUid[album.uid];
            const albumIndex = userAlbums ? userAlbums.findIndex((a) => a.id === album.id) : -1;

            if (userAlbums && albumIndex !== -1) {
                userAlbums[albumIndex] = album;
            } else {
                // Note: This may insert the album in incorrect order, make sure that when we go to the album list
                // page, that we fetch all albums again
                state.albumsByUid[album.uid] = [...(userAlbums ?? []), album];
            }
        });

        builder.addCase(
            deleteAlbum.fulfilled,
            (
                state,
                {
                    meta: {
                        arg: { id: albumId, uid: userId },
                    },
                },
            ) => {
                // Remove album from user albums list
                if (state.albumsByUid[userId]) {
                    state.albumsByUid[userId] = state.albumsByUid[userId].filter((a) => a.id !== albumId);
                }

                // Remove all recent photos that were in this album
                if (state.recentPhotosByUserId?.[userId]) {
                    state.recentPhotosByUserId[userId] = {
                        ...state.recentPhotosByUserId[userId],
                        photos: state.recentPhotosByUserId[userId].photos.filter((p) => p.aid !== albumId),
                    };
                }
            },
        );

        builder.addCase(createAlbum.fulfilled, (state, { payload: album }) => {
            const albums = state.albumsByUid?.[album.uid] ?? [];

            // Insert new album after the profile photo album if it exists
            state.albumsByUid[album.uid] = [
                ...albums.filter((a) => a.type === 'profile'),
                album,
                ...albums.filter((a) => a.type !== 'profile'),
            ];
        });

        builder.addCase(
            movePhotoToAlbum.fulfilled,
            (state, { payload: { photoId, targetAlbumId, sourceAlbumId, userId } }) => {
                // Identify photo if loaded
                const photo =
                    state.albumsByUid?.[userId]
                        .find((a) => a.id === sourceAlbumId)
                        ?.photos?.find((p) => p.id === photoId) ??
                    state.recentPhotosByUserId[userId]?.photos.find((p) => p.id === photoId);

                if (photo) {
                    // Remove from source album's nested photos
                    const userAlbums = state.albumsByUid?.[userId];
                    if (userAlbums) {
                        state.albumsByUid[userId] = removePhotoFromAlbum(userAlbums, photoId, sourceAlbumId);
                    }

                    photo.aid = targetAlbumId;

                    // Append to target album's nested photos
                    state.albumsByUid[userId] = addPhotoToAlbum(state.albumsByUid[userId], photo, targetAlbumId);

                    // Replace photo in recent photos list
                    if (state.recentPhotosByUserId[userId]) {
                        state.recentPhotosByUserId[userId] = {
                            ...state.recentPhotosByUserId[userId],
                            photos: state.recentPhotosByUserId?.[userId].photos.map((p) =>
                                p.id === photoId ? photo : p,
                            ),
                        };
                    }
                }
            },
        );

        builder.addCase(updateAlbumDetails.fulfilled, (state, { payload: album }) => {
            state.albumsByUid[album.uid] = replaceAlbumPreservePhotos(state.albumsByUid?.[album.uid] ?? [], album);
        });

        builder.addCase(updateAlbumPhotosOrder.fulfilled, (state, { payload: album }) => {
            state.albumsByUid[album.uid] = replaceAlbumPhotosOrder(state.albumsByUid?.[album.uid] ?? [], album);
        });

        builder.addCase(uploadPhotoToAlbumNew.fulfilled, (state, { payload: photo }) => {
            if (photo?.aid) {
                state.albumsByUid[photo.uid] = addPhotoToAlbum(state.albumsByUid?.[photo.uid] ?? [], photo, photo.aid);
                if (state.recentPhotosByUserId[photo.uid]) {
                    state.recentPhotosByUserId[photo.uid].photos.unshift(photo);
                    state.recentPhotosByUserId[photo.uid].total =
                        (state.recentPhotosByUserId[photo.uid].total ?? 0) + 1;
                }
            }
        });

        //
        // Photo cases
        //
        builder.addCase(fetchRecentPhotos.fulfilled, (state, { payload: { data: photos }, meta }) => {
            const hasMore = photos.length === (meta.arg.limit ?? RECENT_PHOTOS_FETCH_LIMIT);
            // Replace the whole list if we're fetching the first batch
            if (!meta.arg.offset) {
                state.recentPhotosByUserId[meta.arg.userId] = {
                    ...(state.recentPhotosByUserId[meta.arg.userId] ?? {}),
                    photos,
                    hasMore,
                };
            } else {
                // Otherwise append to the list
                state.recentPhotosByUserId[meta.arg.userId] = {
                    photos: [...state.recentPhotosByUserId[meta.arg.userId].photos, ...photos],
                    hasMore,
                };
            }
        });

        builder.addCase(fetchPhotoCount.fulfilled, (state, { payload: count, meta }) => {
            state.recentPhotosByUserId[meta.arg.userId] = {
                ...(state.recentPhotosByUserId[meta.arg.userId] ?? {}),
                total: count,
            };
        });

        builder.addCase(createLike.fulfilled, (state, { payload: like, meta }) => {
            if (meta.arg.entity_type !== 'photo') return;

            if (!meta.arg.photo) {
                throw new Error('Missing photo in like action arguments');
            }

            updatePhotoLikeStatus(state, meta.arg.photo, true);

            state.likesByPhotoId[meta.arg.photo.id] = {
                likes: [like, ...(state.likesByPhotoId[meta.arg.photo.id]?.likes ?? [])],
                hasMore: state.likesByPhotoId[meta.arg.photo.id]?.hasMore ?? true,
            };
        });

        builder.addCase(deleteLike.fulfilled, (state, { meta }) => {
            if (meta.arg.entity_type !== 'photo') return;

            if (!meta.arg.photo) {
                throw new Error('Missing photo in like action arguments');
            }

            updatePhotoLikeStatus(state, meta.arg.photo, false);

            state.likesByPhotoId[meta.arg.photo.id] = {
                likes:
                    state.likesByPhotoId[meta.arg.photo.id]?.likes.filter((l) => l.liker_uid !== meta.arg.userId) ?? [],
                hasMore: state.likesByPhotoId[meta.arg.photo.id]?.hasMore ?? true,
            };
        });

        builder.addCase(getPhotoLikes.fulfilled, (state, { payload: { data: likes }, meta }) => {
            const hasMore = likes.length === (meta.arg.limit ?? LIKES_FETCH_LIMIT);

            state.likesByPhotoId[meta.arg.photoId] = {
                likes: meta.arg.offset === 0 ? likes : [...state.likesByPhotoId[meta.arg.photoId].likes, ...likes],
                hasMore,
            };
        });

        builder.addMatcher(
            isAnyOf(fetchPhoto.fulfilled, updatePhotoDescription.fulfilled),
            (state, { payload: photo }) => {
                // Replace photo in user recent photos list (if exists)
                if (state.recentPhotosByUserId[photo.uid]) {
                    state.recentPhotosByUserId[photo.uid] = {
                        ...state.recentPhotosByUserId[photo.uid],
                        photos: state.recentPhotosByUserId[photo.uid].photos.map((p) =>
                            p.id === photo.id ? photo : p,
                        ),
                    };
                }

                // Replace photo in nested user album photos (if exists)
                const userAlbums = state.albumsByUid[photo.uid];
                if (userAlbums) {
                    state.albumsByUid[photo.uid] = replacePhotoInAlbum(userAlbums, photo);
                }

                // Insert nested album into albums if not present
                if (photo.album && !state.albumsByUid[photo.uid].find((a) => a.id === photo.aid)) {
                    state.albumsByUid[photo.uid].push(photo.album);
                }
            },
        );

        builder.addMatcher(isAnyOf(deleteAlbumPhoto.fulfilled, moderationDeletePhoto), (state, { payload }) => {
            // Remove from nested photos of user album list
            state.albumsByUid[payload.userId] = removePhotoFromAlbum(
                state.albumsByUid?.[payload.userId] ?? [],
                payload.photoId,
                payload.albumId,
            );

            // Remove from recent photos
            if (state.recentPhotosByUserId[payload.userId]) {
                state.recentPhotosByUserId[payload.userId] = {
                    ...state.recentPhotosByUserId[payload.userId],
                    photos: state.recentPhotosByUserId[payload.userId].photos.filter((p) => p.id !== payload.photoId),
                };
            }
        });
    },
});

export default photoAlbumsSlice.reducer;
